import { useQuery } from "@tanstack/react-query";
import { Schema, z } from "zod";

import { NIKE_URL } from "@/data/constants/url";
import {
  PublicDataQueryKey,
  publicDataQueryKeys,
} from "@/features/stock-mining-pricing/utils/query-keys";
import { useLang } from "@/hooks/use-lang";
import { Lang } from "@/i18n-config";

type Params = {
  itemName: PublicDataQueryKey;
  lang: Lang;
  schema: Schema;
};

const getResponseSchema = (schema: Schema) =>
  z.object({
    data: schema,
    message: z.string(),
  });

export const getPublicData = async <T>({
  itemName,
  lang,
  schema,
}: Params): Promise<T> => {
  const endpoint = `/config/frontend/${itemName}`;
  if (!publicDataQueryKeys.includes(itemName))
    throw new Error(`GET: ${endpoint}\n Invalid itemName: ${itemName}`);
  const res = await fetch(`${NIKE_URL}${endpoint}`, {
    method: "GET",
    headers: { "Accept-Language": lang },
    cache: "no-cache",
  });
  if (!res.ok) throw new Error(`API failed (${endpoint})`);
  return getResponseSchema(schema).parse(await res.json()).data;
};

export const usePublicData = <T>({
  itemName,
  schema,
  enabled = true,
}: Omit<Params, "lang"> & {
  enabled?: boolean;
}) => {
  const { lang } = useLang();

  return useQuery(
    ["public-data", itemName, lang],
    () => getPublicData<T>({ itemName, lang, schema }),
    { enabled }
  );
};
